/**
 * Provider Management (Phase 9F)
 * Lead providers: persisted enable/disable (Master Admin), real connection
 * tests, non-secret settings, bounded search runs and last-run stats.
 * API keys live in the Integration Center vault; this screen only ever
 * receives their masked form.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { 
  Server, 
  CheckCircle2, 
  AlertCircle, 
  XCircle, 
  RefreshCw, 
  Shield, 
  Database,
  FileText,
  Zap
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PROVIDER_ICONS = {
  manual: Database,
  csv: FileText,
  outscraper: Server,
  apify: Zap,
  brightdata: Server,
  google_business: Server
};

const formatDateTime = (value) => (value ? new Date(value).toLocaleString() : '—');

const ExternalProviderPanel = ({ provider, isMaster, authHeaders, onChanged }) => {
  const [settings, setSettings] = useState(provider.settings || {});
  const [form, setForm] = useState({ query: 'medical spa', location: '', max_results: 25 });
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [committing, setCommitting] = useState(false);
  const lastRun = provider.last_run;
  const canRun = provider.enabled && provider.configured;

  const saveSettings = async () => {
    const response = await fetch(`${API}/providers/${provider.id}/settings`, {
      method: 'PUT', headers: authHeaders(), body: JSON.stringify({ settings })
    });
    const data = await response.json().catch(() => ({}));
    if (response.ok) { toast.success('Settings saved'); onChanged(); }
    else toast.error(typeof data.detail === 'string' ? data.detail : 'Invalid settings');
  };

  const runSearch = async () => {
    setRunning(true);
    setResult(null);
    try {
      const response = await fetch(`${API}/providers/${provider.id}/run`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ ...form, max_results: Number(form.max_results) || 25 })
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok) {
        setResult(data);
        toast.success(`${data.run.returned_count} results: ${data.validation_summary.valid} new, ${data.validation_summary.duplicates} duplicates`);
      } else {
        toast.error(typeof data.detail === 'string' ? data.detail : 'Search failed');
      }
    } catch (error) {
      toast.error('Error running search');
    } finally {
      setRunning(false);
      onChanged();
    }
  };

  const commitBatch = async () => {
    setCommitting(true);
    try {
      const response = await fetch(`${API}/lead-acquisition/import/commit`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ batch_id: result.batch_id, column_mapping: result.suggested_mapping })
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok) {
        toast.success(`Imported ${data.created} leads (${data.skipped_duplicates} duplicates skipped)`);
        setResult(null);
      } else {
        toast.error(typeof data.detail === 'string' ? data.detail : 'Import failed');
      }
    } finally {
      setCommitting(false);
    }
  };

  return (
    <div className="space-y-3 pt-2 border-t border-white/10" data-testid={`provider-details-${provider.id}`}>
      {isMaster && (
        <div className="flex items-center justify-between">
          <span className="text-xs text-white/60">API Key:</span>
          <span className="text-xs text-white/80 font-mono">
            {provider.credential ? provider.credential.masked_value : 'Not stored — add it in Integration Center'}
          </span>
        </div>
      )}
      {isMaster && (provider.settings_fields || []).map((field) => (
        <div key={field.key} className="flex items-center gap-2">
          <Input
            className="h-8 text-xs"
            placeholder={`${field.label}${field.required ? ' (required)' : ''}${field.default ? ` — default ${field.default}` : ''}`}
            value={settings[field.key] || ''}
            onChange={(e) => setSettings({ ...settings, [field.key]: e.target.value })}
            data-testid={`provider-setting-${provider.id}-${field.key}`}
          />
          <Button className="ghost-btn text-xs" onClick={saveSettings}>Save</Button>
        </div>
      ))}
      {provider.missing_settings?.length > 0 && (
        <p className="text-xs text-yellow-300/90">Missing: {provider.missing_settings.join(', ')}</p>
      )}

      <div className="text-xs text-white/70 space-y-1" data-testid={`provider-last-run-${provider.id}`}>
        <div className="flex justify-between"><span className="text-white/60">Runs today:</span><span>{provider.runs_today}/{provider.daily_run_limit}</span></div>
        {lastRun ? (
          <>
            <div className="flex justify-between"><span className="text-white/60">Last run:</span><span>{formatDateTime(lastRun.started_at)} · {lastRun.status}</span></div>
            <div className="truncate" title={`${lastRun.query} ${lastRun.location || ''}`}>“{lastRun.query}”{lastRun.location ? ` in ${lastRun.location}` : ''}</div>
            {lastRun.status === 'completed' && (
              <div>{lastRun.returned_count} found · {lastRun.new_count} new · {lastRun.duplicate_count} duplicates · {lastRun.error_count} invalid</div>
            )}
            {lastRun.error && <div className="text-red-300/90">{lastRun.error}</div>}
          </>
        ) : (
          <div className="text-white/50">No runs yet</div>
        )}
      </div>

      {isMaster && (
        <div className="space-y-2">
          <Input className="h-8 text-xs" placeholder="Category / keyword" value={form.query}
            onChange={(e) => setForm({ ...form, query: e.target.value })} data-testid={`provider-run-query-${provider.id}`} />
          <div className="flex gap-2">
            <Input className="h-8 text-xs" placeholder="Location (e.g. Austin, TX)" value={form.location}
              onChange={(e) => setForm({ ...form, location: e.target.value })} data-testid={`provider-run-location-${provider.id}`} />
            <Input className="h-8 text-xs w-20" type="number" min={1} max={provider.max_results_cap} value={form.max_results}
              onChange={(e) => setForm({ ...form, max_results: e.target.value })} data-testid={`provider-run-max-${provider.id}`} />
          </div>
          <Button className="w-full ghost-btn text-xs" onClick={runSearch} disabled={!canRun || running || form.query.trim().length < 2}
            data-testid={`provider-run-${provider.id}`}>
            {running ? <><RefreshCw size={14} className="animate-spin" /> Searching...</> : <><Zap size={14} /> Run Search (max {provider.max_results_cap})</>}
          </Button>
          {!canRun && <p className="text-xs text-white/50">{provider.enabled ? 'Configure the provider to run searches' : 'Enable the provider to run searches'}</p>}
          {result?.batch_id && result.validation_summary.valid > 0 && (
            <Button className="w-full primary-btn text-xs" onClick={commitBatch} disabled={committing} data-testid={`provider-commit-${provider.id}`}>
              Import {result.validation_summary.valid} new leads into Lead Database
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

const ProviderManagement = ({ user }) => {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState({});
  const isMaster = user.role === 'Master Administrator';

  const authHeaders = useCallback(() => ({
    'Authorization': `Bearer ${user.token}`,
    'Content-Type': 'application/json'
  }), [user.token]);

  const fetchProviders = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/providers`, {
        headers: authHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setProviders(data.providers || []);
      }
    } catch (error) {
      console.error('Error fetching providers:', error);
      toast.error('Failed to load providers');
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => {
    fetchProviders();
  }, [fetchProviders]);

  const handleTestConnection = async (providerId) => {
    setTesting((current) => ({ ...current, [providerId]: true }));
    try {
      const response = await fetch(`${API}/providers/${providerId}/test`, {
        method: 'POST',
        headers: authHeaders()
      });

      const data = await response.json().catch(() => ({}));
      if (response.ok && data.success !== false) {
        toast.success(data.message || 'Connection test successful');
      } else {
        toast.error(data.message || (typeof data.detail === 'string' ? data.detail : 'Connection test failed'));
      }
      await fetchProviders();
    } catch (error) {
      toast.error('Error testing connection');
    } finally {
      setTesting((current) => ({ ...current, [providerId]: false }));
    }
  };

  const handleToggle = async (provider, enabled) => {
    try {
      const response = await fetch(`${API}/providers/${provider.id}/toggle`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ enabled })
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok) {
        toast.success(`${provider.name} ${enabled ? 'enabled' : 'disabled'}`);
      } else {
        toast.error(typeof data.detail === 'string' ? data.detail : 'Could not change provider state');
      }
    } catch (error) {
      toast.error('Error changing provider state');
    } finally {
      await fetchProviders();
    }
  };

  const getHealthBadge = (status) => {
    switch (status) {
      case 'invalid_credentials':
        return <Badge className="bg-red-500/20 text-red-300 border-red-500/30 flex items-center gap-1">
          <XCircle size={12} />
          Invalid Credentials
        </Badge>;
      case 'error':
        return <Badge className="bg-red-500/20 text-red-300 border-red-500/30 flex items-center gap-1">
          <AlertCircle size={12} />
          Error
        </Badge>;
      case 'not_implemented':
        return <Badge className="bg-gray-500/20 text-gray-300 border-gray-500/30 flex items-center gap-1">
          <AlertCircle size={12} />
          Not Implemented
        </Badge>;
      case 'healthy':
        return <Badge className="bg-green-500/20 text-green-300 border-green-500/30 flex items-center gap-1">
          <CheckCircle2 size={12} />
          Healthy
        </Badge>;
      case 'pending':
        return <Badge className="bg-yellow-500/20 text-yellow-300 border-yellow-500/30 flex items-center gap-1">
          <AlertCircle size={12} />
          Needs Config
        </Badge>;
      case 'not_configured':
        return <Badge className="bg-gray-500/20 text-gray-300 border-gray-500/30 flex items-center gap-1">
          <AlertCircle size={12} />
          Not Configured
        </Badge>;
      case 'unknown':
      default:
        return <Badge className="bg-white/10 text-white/60 border-white/20 flex items-center gap-1">
          <XCircle size={12} />
          Unknown
        </Badge>;
    }
  };

  return (
    <div className="page-stack" data-testid="provider-management">
      {/* Header */}
      <section className="hero-panel ceo-hero glass-panel noise-overlay">
        <div>
          <p className="eyebrow">Phase 9F — Lead Provider Architecture</p>
          <h2>Provider Management</h2>
          <div className="hero-stats">
            <span>{providers.filter(p => p.enabled).length} Active</span>
            <span>{providers.filter(p => p.health_status === 'healthy').length} Healthy</span>
            <span>{providers.length} Total Providers</span>
          </div>
        </div>
        <Button 
          className="ghost-btn" 
          onClick={fetchProviders}
          data-testid="refresh-providers-btn"
        >
          <RefreshCw size={16} />
          Refresh
        </Button>
      </section>

      {/* Provider Cards */}
      <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {loading ? (
          [...Array(5)].map((_, i) => (
            <Card key={i} className="glass-panel border-white/10">
              <CardContent className="pt-6">
                <Skeleton className="h-32 w-full bg-white/10" />
              </CardContent>
            </Card>
          ))
        ) : providers.length === 0 ? (
          <div className="col-span-full text-center py-12">
            <Server className="h-12 w-12 text-white/20 mx-auto mb-3" />
            <p className="text-white/60">No providers configured</p>
          </div>
        ) : (
          providers.map((provider) => {
            const Icon = PROVIDER_ICONS[provider.id] || Server;
            return (
              <Card 
                key={provider.id} 
                className="glass-panel border-white/10 hover:border-[var(--line-gold)] transition-all"
                data-testid={`provider-card-${provider.id}`}
              >
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`h-12 w-12 rounded-full bg-white/10 flex items-center justify-center ${provider.enabled ? 'text-[var(--gold)]' : 'text-white/40'}`}>
                        <Icon className="h-6 w-6" />
                      </div>
                      <div>
                        <CardTitle className="text-white/90 text-base">{provider.name}</CardTitle>
                        <p className="text-xs text-white/60 mt-1">
                          {provider.requires_api_key ? 'Requires API Key' : 'No API Key'}
                        </p>
                      </div>
                    </div>
                    <Switch
                      checked={provider.enabled}
                      disabled={!provider.toggleable || !isMaster}
                      onCheckedChange={(checked) => handleToggle(provider, checked)}
                      title={provider.toggleable ? (isMaster ? '' : 'Only the Master Administrator can change this') : 'Built in — always on'}
                      data-testid={`provider-toggle-${provider.id}`}
                    />
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Health Status */}
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-white/60">Health Status:</span>
                    {getHealthBadge(provider.health_status)}
                  </div>

                  {/* Last Tested */}
                  {provider.last_tested_at && (
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-white/60">Last Tested:</span>
                      <span className="text-xs text-white/80">
                        {formatDateTime(provider.last_tested_at)}
                      </span>
                    </div>
                  )}
                  {provider.last_test_message && (
                    <p className="text-xs text-white/60" data-testid={`provider-test-message-${provider.id}`}>{provider.last_test_message}</p>
                  )}

                  {/* Status Badge */}
                  <div className="pt-2 border-t border-white/10">
                    <Badge 
                      className={`w-full justify-center ${provider.enabled ? 'bg-[var(--gold)]/20 text-[var(--gold)]' : 'bg-white/10 text-white/60'}`}
                    >
                      {provider.enabled ? 'Active' : 'Disabled'}
                    </Badge>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2">
                    <Button
                      className="flex-1 ghost-btn text-xs"
                      onClick={() => handleTestConnection(provider.id)}
                      disabled={testing[provider.id] || (provider.kind === 'external' && !isMaster)}
                      data-testid={`test-connection-${provider.id}`}
                    >
                      {testing[provider.id] ? (
                        <>
                          <RefreshCw size={14} className="animate-spin" />
                          Testing...
                        </>
                      ) : (
                        <>
                          <CheckCircle2 size={14} />
                          Test Connection
                        </>
                      )}
                    </Button>
                  </div>

                  {provider.kind === 'external' && (
                    <ExternalProviderPanel
                      provider={provider}
                      isMaster={isMaster}
                      authHeaders={authHeaders}
                      onChanged={fetchProviders}
                    />
                  )}

                  {/* Security Note */}
                  {provider.requires_api_key && (
                    <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                      <div className="flex items-start gap-2">
                        <Shield className="h-4 w-4 text-blue-400 mt-0.5 flex-shrink-0" />
                        <p className="text-xs text-blue-300/90">
                          API keys are stored securely on the backend and never exposed to the frontend
                        </p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })
        )}
      </section>

      {/* Info Panel */}
      <section className="glass-panel">
        <div className="flex items-start gap-4">
          <Shield className="h-6 w-6 text-[var(--gold)] flex-shrink-0 mt-1" />
          <div>
            <h3 className="text-lg font-semibold text-white/90 mb-2">Provider Security</h3>
            <ul className="space-y-2 text-sm text-white/70">
              <li className="flex items-start gap-2">
                <CheckCircle2 className="h-4 w-4 text-green-400 mt-0.5 flex-shrink-0" />
                <span>All provider API keys are encrypted and stored server-side only</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="h-4 w-4 text-green-400 mt-0.5 flex-shrink-0" />
                <span>Frontend never receives or stores sensitive credentials</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="h-4 w-4 text-green-400 mt-0.5 flex-shrink-0" />
                <span>All provider actions are logged for compliance and audit purposes</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="h-4 w-4 text-green-400 mt-0.5 flex-shrink-0" />
                <span>Master Admin can enable/disable providers and configure settings</span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* Provider Status Summary */}
      <section className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="glass-panel border-white/10">
          <CardHeader>
            <CardTitle className="text-white/90 text-sm flex items-center gap-2">
              <Database className="h-4 w-4 text-[var(--gold)]" />
              Manual & CSV
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-[var(--gold)] mb-1">
              {providers.filter(p => ['manual', 'csv'].includes(p.id) && p.enabled).length}/2
            </p>
            <p className="text-xs text-white/60">Core Providers Active</p>
          </CardContent>
        </Card>

        <Card className="glass-panel border-white/10">
          <CardHeader>
            <CardTitle className="text-white/90 text-sm flex items-center gap-2">
              <Server className="h-4 w-4 text-[var(--gold)]" />
              External Providers
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-white/90 mb-1">
              {providers.filter(p => !['manual', 'csv'].includes(p.id) && p.enabled).length}/{providers.filter(p => !['manual', 'csv'].includes(p.id)).length}
            </p>
            <p className="text-xs text-white/60">External Integrations</p>
          </CardContent>
        </Card>

        <Card className="glass-panel border-white/10">
          <CardHeader>
            <CardTitle className="text-white/90 text-sm flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-[var(--gold)]" />
              Health Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-green-400 mb-1">
              {providers.filter(p => p.health_status === 'healthy').length}/{providers.length}
            </p>
            <p className="text-xs text-white/60">Healthy Providers</p>
          </CardContent>
        </Card>
      </section>
    </div>
  );
};

export default ProviderManagement;
